import React, { useEffect, useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import StarRating from '../StarRating/StarRating';
import Banner from '../Banner/Banner';
import Button from '../Button/Button';
import SectionPliable from '../SectionPliable/SectionPliable';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { useAuth } from '../../contexts/AuthContext';
import AvisPDF from '../../pages/PDF/AvisPDF';
import { AvisData, COMMENTAIRE_MAX, fmtDateFr, lotTechniqueLabel, lotGeoLabel, panierLabel } from './avisTypes';
import './AvisPrestataire.css';

// Qui est NOTE : 'prestataire' = avis public acheteur -> prestataire (existant) ; 'acheteur' =
// miroir PRIVE prestataire -> acheteur (roles inverses, visible des 2 parties concernees seules).
export type EvalCible = 'prestataire' | 'acheteur';

// En-tete de la carte : nom de la contrepartie (l'autre acteur) puis lot technique / lot geo /
// numero de panier (id d'operation) issus du snapshot.
const AvisHeader: React.FC<{ avis: AvisData; canEdit: boolean; cible: EvalCible }> = ({ avis, canEdit, cible }) => {
  // cible acheteur : l'editeur est le prestataire (il voit l'acheteur note) -> roles inverses.
  const name = cible === 'acheteur'
    ? ((canEdit ? avis.organisme_nom : avis.entite_nom) ?? (canEdit ? 'Acheteur' : 'Prestataire'))
    : ((canEdit ? avis.entite_nom : avis.organisme_nom) ?? (canEdit ? 'Prestataire' : 'Acheteur'));
  return (
    <div className="avis-eval-head">
      <span className="avis-eval-name">{name}</span>
      <div className="avis-ligne-ids">
        <span className="avis-ligne-id"><span className="avis-ligne-id-k">Lot technique</span>{lotTechniqueLabel(avis)}</span>
        <span className="avis-ligne-id"><span className="avis-ligne-id-k">Lot géographique</span>{lotGeoLabel(avis)}</span>
        <span className="avis-ligne-id"><span className="avis-ligne-id-k">Panier</span>{panierLabel(avis)}</span>
      </div>
    </div>
  );
};

interface EvaluationPrestataireProps {
  avis: AvisData | null;
  // Le rater edite ; l'autre partie est en lecture seule. (cible prestataire : rater = acheteur ;
  // cible acheteur : rater = prestataire.) Le serveur tranche via can_edit.
  canEdit: boolean;
  loading?: boolean;
  saving?: boolean;
  onSave: (note: number, commentaire: string) => void;
  // Qui est note. Defaut 'prestataire' (comportement existant inchange).
  cible?: EvalCible;
}

// cible prestataire (public) : avis acheteur -> prestataire, "visibles publiquement".
const NOTICE_ACHETEUR = "Par défaut, il est attribué automatiquement la note maximale au prestataire lors de la commande. Il vous incombe de modifier cette note et l'avis associé à tout moment, en fonction de la capacité du prestataire à mener à bien l'opération. Ces informations sont visibles publiquement.";
const NOTICE_PRESTATAIRE = "Par défaut, il vous est attribué automatiquement la note maximale lors de la commande. Mais l'acheteur peut modifier cette note et l'avis associé à tout moment, en fonction de votre capacité à mener à bien l'opération. Ces informations sont visibles publiquement.";

// cible acheteur (PRIVE) : avis prestataire -> acheteur, "restent privées" (2 parties seules).
const NOTICE_ACHETEUR_CIBLE_EDIT = "Par défaut, vous attribuez automatiquement la note maximale à l'acheteur lors de la commande. Vous pouvez modifier cette note et l'avis associé à tout moment, en fonction du déroulé de la collaboration (clarté du besoin, délais de paiement, réactivité). Ces informations restent privées : seuls vous et l'acheteur concerné y ont accès.";
const NOTICE_ACHETEUR_CIBLE_RO = "Par défaut, le prestataire vous attribue automatiquement la note maximale lors de la commande. Il peut modifier cette note et l'avis associé à tout moment, en fonction du déroulé de la collaboration. Ces informations restent privées : seuls vous et le prestataire concerné y ont accès.";

/**
 * Onglet "Évaluation" d'une commande validee. Une carte (meme rendu que le profil, sans accordeon)
 * sous un titre uniforme. Le rater edite (auto-enregistrement debounce 1 s + indicateur d'etat),
 * l'autre partie est en lecture seule ; export PDF. `cible` choisit le sens de la notation :
 * prestataire note (public) ou acheteur note (miroir prive).
 */
const EvaluationPrestataire: React.FC<EvaluationPrestataireProps> = ({ avis, canEdit, loading, saving, onSave, cible = 'prestataire' }) => {
  const { user } = useAuth();
  const [note, setNote] = useState<number>(avis?.note ?? 5);
  const [commentaire, setCommentaire] = useState<string>(avis?.commentaire ?? '');
  const [printing, setPrinting] = useState(false);
  // Confirmation "enregistre" : affichee brievement quand une sauvegarde vient de se terminer.
  const [showSaved, setShowSaved] = useState(false);
  const wasSaving = useRef(false);
  // Derniere valeur de commentaire ENVOYEE au serveur. Sert de reference (et non le champ qui bouge)
  // pour decider s'il y a quelque chose a enregistrer -> evite la boucle "Enregistrement" infinie.
  const lastSavedComment = useRef(avis?.commentaire ?? '');

  const isCibleAcheteur = cible === 'acheteur';

  // Resynchronise le champ UNIQUEMENT au changement de commande (avis.id) : on ne reecrit jamais
  // le textarea pendant la frappe (sinon l'echo serveur ecrase la saisie en cours).
  useEffect(() => {
    setNote(avis?.note ?? 5);
    setCommentaire(avis?.commentaire ?? '');
    lastSavedComment.current = avis?.commentaire ?? '';
  }, [avis?.id]);

  useEffect(() => {
    if (wasSaving.current && !saving) {
      setShowSaved(true);
      const timer = setTimeout(() => setShowSaved(false), 2500);
      wasSaving.current = Boolean(saving);
      return () => clearTimeout(timer);
    }
    wasSaving.current = Boolean(saving);
  }, [saving]);

  // Marque le commentaire comme "envoye" puis declenche la sauvegarde (note + commentaire).
  const persist = (n: number, comment: string) => {
    lastSavedComment.current = comment;
    onSave(n, comment);
  };

  // Auto-enregistrement : 1 s apres la derniere frappe (plus besoin de cliquer hors du champ).
  // Gate sur `saving` : on n'empile pas un nouveau save tant qu'un est en cours. La comparaison se
  // fait sur la DERNIERE valeur envoyee (ref), donc une fois envoyee la boucle s'arrete.
  useEffect(() => {
    if (!canEdit || !avis || saving || commentaire === lastSavedComment.current) return;
    const timer = setTimeout(() => persist(note, commentaire), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentaire, canEdit, avis?.id, saving]);

  const handleStar = (n: number) => { setNote(n); persist(n, commentaire); };
  // Filet de securite : si l'utilisateur quitte le champ avant les 3 s, on enregistre tout de suite.
  const handleBlur = () => { if (lastSavedComment.current !== commentaire) persist(note, commentaire); };

  // Impression PDF de CET avis (reutilise l'export des avis, avec un seul element).
  const handlePrint = async () => {
    if (!avis || printing) return;
    setPrinting(true);
    try {
      const blob = await pdf(
        <AvisPDF
          avis={[avis]}
          cote={canEdit ? 'acheteur' : 'prestataire'}
          generatedAt={new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          generatedBy={user?.name || user?.email || '—'}
        />,
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avis-${isCibleAcheteur ? 'acheteur' : 'prestataire'}-${avis.type_commande.toLowerCase()}-${avis.operation_id ?? avis.commande_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Une erreur est survenue lors de la génération du PDF de l'avis.");
    } finally {
      setPrinting(false);
    }
  };

  // Textes selon la cible notee (prestataire = avis public existant ; acheteur = miroir prive).
  const title = isCibleAcheteur
    ? 'Avis des prestataires concernant les acheteurs'
    : 'Avis des acheteurs concernant les prestataires';
  const notice = isCibleAcheteur
    ? (canEdit ? NOTICE_ACHETEUR_CIBLE_EDIT : NOTICE_ACHETEUR_CIBLE_RO)
    : (canEdit ? NOTICE_ACHETEUR : NOTICE_PRESTATAIRE);
  const rateAria = isCibleAcheteur ? "Noter l'acheteur" : 'Noter le prestataire';
  const editPlaceholder = isCibleAcheteur ? "Votre avis sur l'acheteur…" : 'Modifiez votre avis sur le prestataire…';
  const emptyEditMsg = 'Aucune commande à évaluer.';
  const emptyRoMsg = isCibleAcheteur
    ? "Aucun avis pour le moment. Le prestataire n'a pas encore déposé son évaluation."
    : "Aucun avis pour le moment. L'acheteur n'a pas encore déposé son évaluation.";

  // Indicateur d'enregistrement (visible) : en attente (frappe) -> enregistrement -> enregistre.
  const hasUnsaved = Boolean(canEdit && avis && commentaire !== lastSavedComment.current);
  // Avis "donné" = l'utilisateur a écrit un commentaire (la note est pré-remplie à 5 par défaut,
  // donc seule la présence d'un commentaire prouve un avis réellement rédigé). Pilote l'accent visuel
  // qui attire l'oeil sur la section éditable tant qu'elle n'est pas complétée.
  const isGiven = Boolean(canEdit && avis && commentaire.trim().length > 0);
  const saveState: 'saving' | 'pending' | 'saved' | 'idle' =
    saving ? 'saving' : hasUnsaved ? 'pending' : showSaved ? 'saved' : 'idle';
  const saveLabel = saveState === 'saving' ? 'Enregistrement…'
    : saveState === 'pending' ? 'Enregistrement automatique…'
    : saveState === 'saved' ? 'Enregistré ✓' : '';

  const printButton = (
    <div className="avis-eval-actions">
      <Button
        value={printing ? 'Préparation…' : "Imprimer l'avis"}
        icon={<PdfIcon />}
        variant="secondary"
        size="small"
        onClick={handlePrint}
        disabled={printing}
      />
    </div>
  );

  return (
    <div className="avis-eval">
      <SectionPliable title={title}>
        <Banner variant="info" className="avis-eval-notice" message={notice} />

        {loading ? (
          <div className="avis-eval-card"><StarRating value={null} loading /></div>
        ) : !avis ? (
          <p className="avis-eval-empty">{canEdit ? emptyEditMsg : emptyRoMsg}</p>
        ) : canEdit ? (
          // Rater : carte editable mise en avant (accent orange tant que pas de commentaire) pour
          // attirer l'oeil sur la section que l'utilisateur doit remplir ; passe en "donné" (vert) ensuite.
          <div className={`avis-eval-card ${isGiven ? 'avis-eval-card--given' : 'avis-eval-card--actionable'}`}>
            <span className={`avis-eval-badge ${isGiven ? 'avis-eval-badge--done' : 'avis-eval-badge--todo'}`}>
              {isGiven ? 'Votre avis · donné ✓' : 'Votre avis · à compléter'}
            </span>
            <AvisHeader avis={avis} canEdit={canEdit} cible={cible} />
            <div className="avis-eval-rate">
              <span className="avis-eval-rate-label">Votre note</span>
              <div className="avis-eval-rate-stars">
                <StarRating value={note} onRate={handleStar} ariaLabel={rateAria} />
                <span className="avis-eval-rate-val">{note}/5</span>
              </div>
              <span className="avis-eval-rate-hint">Cliquez sur les étoiles pour modifier votre note.</span>
            </div>
            <textarea
              className="avis-eval-textarea"
              value={commentaire}
              maxLength={COMMENTAIRE_MAX}
              onChange={(e) => setCommentaire(e.target.value)}
              onBlur={handleBlur}
              placeholder={editPlaceholder}
            />
            <div className="avis-eval-foot">
              <span className="avis-eval-count">{commentaire.length}/{COMMENTAIRE_MAX} caractères</span>
              {saveState !== 'idle' && (
                <span className={`avis-eval-save avis-eval-save--${saveState}`}>{saveLabel}</span>
              )}
            </div>
            {printButton}
          </div>
        ) : (
          // Autre partie : carte en lecture seule (note + commentaire).
          <div className="avis-eval-card">
            <AvisHeader avis={avis} canEdit={canEdit} cible={cible} />
            <span className="avis-ligne-note">
              <StarRating value={avis.note} readOnly />
              <span className="avis-ligne-note-val">{avis.note}/5</span>
              {avis.updated_at && <span className="avis-ligne-date"> · Avis du {fmtDateFr(avis.updated_at)}</span>}
            </span>
            {avis.commentaire && <p className="avis-ligne-comment">« {avis.commentaire} »</p>}
            {printButton}
          </div>
        )}
      </SectionPliable>
    </div>
  );
};

export default EvaluationPrestataire;
